import React from 'react';
import { Delta, priorRange } from '@/components/shared';
import { Env, ErrorBanner, Spinner } from '../../shared';
import { useAdminFetch } from '../../shared/useAdminFetch';
import { Panel, StatTile } from '../../gamification/components/StatTile';
import { useClearReviewFlag, useReferralCodeDetail } from '../api/useReferralCodes';
import { AttributionRow, DealGrantRow, ReferralCode, ReferralCodeDetail } from '../api/types';
import { formatCents, formatCount, formatDateTime } from '../format';
import {
  CodeKindBadge,
  CodeStatusBadge,
  DateRange,
  DateRangeFilter,
  EmptyRow,
  ReviewFlagBadge,
  TableWrap,
  tdClass,
  tdNumClass,
  thClass,
  thNumClass,
} from './controls';

function SignupName({ row }: { row: AttributionRow }) {
  if (!row.new_user_id) {
    // V102 nulls the user on account deletion so the payout snapshot survives.
    return <span className="text-xs text-gray-500 italic">deleted account</span>;
  }
  return (
    <div className="min-w-0">
      <p className="text-xs text-gray-200 truncate">{row.display_name || row.email || 'no display name'}</p>
      <p className="font-mono text-[11px] text-gray-500 truncate">{row.new_user_id}</p>
    </div>
  );
}

function FlaggedTable({
  rows,
  truncated,
  env,
  onCleared,
}: {
  rows: AttributionRow[];
  truncated: boolean;
  env: Env;
  onCleared: () => void;
}) {
  const { clearFlag, clearing, error } = useClearReviewFlag(env);

  const onClear = async (id: string) => {
    if (await clearFlag(id)) onCleared();
  };

  return (
    <>
      {error && <ErrorBanner message={error} />}
      <TableWrap>
        <thead className="bg-gray-900/60">
          <tr>
            <th className={thClass}>Signup</th>
            <th className={thClass}>Flag</th>
            <th className={thClass}>Status</th>
            <th className={thClass}>Device</th>
            <th className={thClass}>IP</th>
            <th className={thNumClass}>Rate</th>
            <th className={thClass}>Payout</th>
            <th className={thClass}>Counted</th>
            <th className={thClass} />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {rows.length === 0 && <EmptyRow span={9}>Nothing flagged for review in this window.</EmptyRow>}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-900/40">
              <td className={tdClass}>
                <SignupName row={r} />
              </td>
              <td className={tdClass}>
                <ReviewFlagBadge flag={r.review_flag} />
              </td>
              <td className={tdClass}>
                <span className="text-xs text-gray-400">{r.status}</span>
              </td>
              <td className={tdClass}>
                <span className="font-mono text-[11px] text-gray-400 break-all">{r.device_id || '—'}</span>
              </td>
              <td className={tdClass}>
                <span className="font-mono text-[11px] text-gray-400">{r.source_ip || '—'}</span>
              </td>
              <td className={tdNumClass}>{formatCents(r.payout_rate_cents)}</td>
              <td className={tdClass}>
                <span className={`text-xs ${r.payout_eligible ? 'text-gray-400' : 'text-gray-600'}`}>
                  {r.payout_eligible ? 'eligible' : 'not eligible'}
                </span>
              </td>
              <td className={tdClass}>
                <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(r.counted_at)}</span>
              </td>
              <td className={tdClass}>
                {/* Clearing restores this row to the $ owed aggregate. It is the
                    only way back from an automatic flag, so it stays one click
                    from the evidence rather than behind a separate screen. */}
                <button
                  type="button"
                  disabled={clearing === r.id}
                  onClick={() => onClear(r.id)}
                  className="px-2 py-1 text-xs font-medium rounded-md border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 disabled:opacity-40 whitespace-nowrap"
                >
                  {clearing === r.id ? 'Clearing…' : 'Clear flag'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {truncated && (
        <p className="text-xs text-amber-400 mt-2">
          Showing the first 200 flagged rows — narrow the window to see the rest.
        </p>
      )}
    </>
  );
}

function SignupsTable({ rows, truncated }: { rows: AttributionRow[]; truncated: boolean }) {
  return (
    <>
      <TableWrap>
        <thead className="bg-gray-900/60">
          <tr>
            <th className={thClass}>Signup</th>
            <th className={thClass}>Status</th>
            <th className={thClass}>Entered via</th>
            <th className={thNumClass}>Rate</th>
            <th className={thClass}>Payout</th>
            <th className={thClass}>Flag</th>
            <th className={thClass}>Counted</th>
            <th className={thClass}>CRM sync</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-800">
          {rows.length === 0 && <EmptyRow span={8}>No signups counted in this window.</EmptyRow>}
          {rows.map((r) => (
            <tr key={r.id} className="hover:bg-gray-900/40">
              <td className={tdClass}>
                <SignupName row={r} />
              </td>
              <td className={tdClass}>
                <span className="text-xs text-gray-400">{r.status}</span>
              </td>
              <td className={tdClass}>
                <span className="text-xs text-gray-400">{r.entered_via || '—'}</span>
              </td>
              <td className={tdNumClass}>{formatCents(r.payout_rate_cents)}</td>
              <td className={tdClass}>
                <span className={`text-xs ${r.payout_eligible ? 'text-gray-400' : 'text-gray-600'}`}>
                  {r.payout_eligible ? 'eligible' : 'not eligible'}
                </span>
              </td>
              <td className={tdClass}>{r.review_flag ? <ReviewFlagBadge flag={r.review_flag} /> : <span className="text-xs text-gray-600">—</span>}</td>
              <td className={tdClass}>
                <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(r.counted_at)}</span>
              </td>
              <td className={tdClass}>
                <span className="text-xs text-gray-500 whitespace-nowrap">
                  {r.synced_to_crm_at ? formatDateTime(r.synced_to_crm_at) : 'not synced'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {truncated && (
        <p className="text-xs text-gray-500 mt-2">Showing the 100 most recent signups in this window.</p>
      )}
    </>
  );
}

function GrantsTable({ rows }: { rows: DealGrantRow[] }) {
  return (
    <TableWrap>
      <thead className="bg-gray-900/60">
        <tr>
          <th className={thClass}>User</th>
          <th className={thClass}>Campaign</th>
          <th className={thClass}>Status</th>
          <th className={thClass}>Granted</th>
          <th className={thClass}>Expires</th>
          <th className={thClass}>Redeemed</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-800">
        {rows.length === 0 && <EmptyRow span={6}>No deals granted off this code in this window.</EmptyRow>}
        {rows.map((g) => (
          <tr key={g.id} className="hover:bg-gray-900/40">
            <td className={tdClass}>
              <div className="min-w-0">
                <p className="text-xs text-gray-200 truncate">{g.display_name || 'no display name'}</p>
                {g.user_id && <p className="font-mono text-[11px] text-gray-500 truncate">{g.user_id}</p>}
              </div>
            </td>
            <td className={tdClass}>
              <span className="text-xs text-gray-400">{g.campaign_name || '—'}</span>
            </td>
            <td className={tdClass}>
              <span className={`text-xs ${g.status === 'used' ? 'text-emerald-300' : 'text-gray-400'}`}>{g.status}</span>
            </td>
            <td className={tdClass}>
              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(g.granted_at)}</span>
            </td>
            <td className={tdClass}>
              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(g.expires_at)}</span>
            </td>
            <td className={tdClass}>
              <span className="text-xs text-gray-400 whitespace-nowrap">{formatDateTime(g.redeemed_at)}</span>
            </td>
          </tr>
        ))}
      </tbody>
    </TableWrap>
  );
}

/**
 * Per-code dashboard: the window aggregate (verified signups, $ owed, redeemed
 * deals, review-flagged count) plus the rows behind those numbers.
 *
 * `$ owed` is the sum of the payout-rate SNAPSHOT on each eligible, non-flagged,
 * non-void attribution — editing the code's rate does not move it retroactively.
 * Flagged rows are excluded from it, which is why the flagged table is a review
 * queue and not just a report.
 */
export function CodeDashboard({
  env,
  code,
  range,
  onRangeChange,
  onBack,
  onEdit,
}: {
  env: Env;
  code: string;
  range: DateRange;
  onRangeChange: (r: DateRange) => void;
  onBack: () => void;
  /** Receives the loaded row so the edit form opens pre-filled. */
  onEdit: (code: ReferralCode) => void;
}) {
  const { data, loading, error, refetch } = useReferralCodeDetail(
    env,
    code,
    range.from || undefined,
    range.to || undefined,
  );
  const before = priorRange(range.from || null, range.to || null);
  const prior = useAdminFetch<ReferralCodeDetail>(
    before && {
      env,
      path: `/admin/referrals/codes/${encodeURIComponent(code)}`,
      query: { from: before[0], to: before[1] },
    },
  )?.stats;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={onBack}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            ← All codes
          </button>
          <h3 className="font-mono text-lg font-semibold text-white">{code}</h3>
          {data && <CodeKindBadge kind={data.kind} />}
          {data && <CodeStatusBadge status={data.status} />}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refetch}
            className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={() => data && onEdit(data)}
            disabled={!data}
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
          >
            Edit code
          </button>
        </div>
      </div>

      <DateRangeFilter value={range} onChange={onRangeChange} />

      {error && <ErrorBanner message={error} />}

      {loading && (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      )}

      {!loading && data && (
        <>
          {/* Identity */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Promoter</p>
              {data.owner ? (
                <>
                  <p className="mt-2 text-white truncate">
                    {data.owner.display_name || data.owner.email || 'no display name'}
                  </p>
                  <p className="font-mono text-[11px] text-gray-500 truncate">{data.owner.user_id}</p>
                </>
              ) : (
                <p className="mt-2 text-gray-500">
                  {data.kind === 'campaign' ? 'campaign code — pays nobody' : 'unbound'}
                </p>
              )}
              {data.ambassador_ref && (
                <p className="mt-1 text-xs text-violet-300">tag: {data.ambassador_ref}</p>
              )}
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Deal</p>
              <p className="mt-2 text-white truncate">{data.deal_campaign?.name ?? 'fallback deal'}</p>
              <p className="mt-1 text-xs text-gray-500">
                {data.deal_campaign
                  ? `${data.deal_campaign.kind} · ${data.deal_campaign.status}`
                  : 'resolved to personal_default at grant time'}
              </p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Payout rate</p>
              <p className="mt-2 text-xl font-bold text-white tabular-nums">
                {formatCents(data.payout_rate_cents)}
              </p>
              <p className="mt-1 text-xs text-gray-500">per signup, snapshotted at signup</p>
            </div>
            <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Created</p>
              <p className="mt-2 text-white">{formatDateTime(data.created_at)}</p>
              {data.created_by && (
                <p className="font-mono text-[11px] text-gray-500 truncate">by {data.created_by}</p>
              )}
            </div>
          </div>

          {/* Window aggregate */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <StatTile
              label="Verified signups"
              info="ref_signups_verified"
              value={formatCount(data.stats.signups_verified)}
              delta={<Delta current={data.stats.signups_verified} prior={prior?.signups_verified} />}
            />
            <StatTile
              label="Signups counted"
              info="ref_signups_total"
              value={formatCount(data.stats.signups_total)}
              delta={<Delta current={data.stats.signups_total} prior={prior?.signups_total} />}
              sublabel="excludes voided"
            />
            <StatTile
              label="$ owed"
              info="ref_owed"
              value={formatCents(data.stats.owed_cents)}
              delta={<Delta current={data.stats.owed_cents} prior={prior?.owed_cents} />}
              sublabel="eligible, non-flagged"
            />
            <StatTile
              label="$ owed (verified)"
              info="ref_owed_verified"
              value={formatCents(data.stats.owed_verified_cents)}
              delta={<Delta current={data.stats.owed_verified_cents} prior={prior?.owed_verified_cents} />}
              sublabel="conservative basis"
            />
            <StatTile
              label="Deals redeemed"
              info="ref_deals_redeemed"
              value={formatCount(data.stats.redeemed_deal_count)}
              delta={<Delta current={data.stats.redeemed_deal_count} prior={prior?.redeemed_deal_count} />}
              sublabel={`${formatCount(data.stats.granted_deal_count)} granted`}
            />
            <StatTile
              label="Review-flagged"
              info="ref_review_flagged"
              value={formatCount(data.stats.review_flagged_count)}
              tone={data.stats.review_flagged_count > 0 ? 'warn' : 'default'}
              sublabel="held out of $ owed"
            />
          </div>

          <Panel
            title="Review-flagged signups"
            subtitle="Held out of $ owed until adjudicated. Device and IP are the anti-farming signals."
          >
            <FlaggedTable
              rows={data.review_flagged}
              truncated={data.review_flagged_truncated}
              env={env}
              onCleared={refetch}
            />
          </Panel>

          <Panel title="Deal grants" subtitle="Deals issued off this code, newest first — the redeemed count in detail.">
            <GrantsTable rows={data.deal_grants} />
          </Panel>

          <Panel title="Recent signups" subtitle="Attribution rows counted in this window, newest first.">
            <SignupsTable rows={data.recent_signups} truncated={data.recent_signups_truncated} />
          </Panel>
        </>
      )}
    </div>
  );
}
